import { i18nScope, metaKey } from '~/scripts/utils';
import { BONKERS_LD_DATA } from '~/constants/ldData';
import { PRECONNECT } from '~/constants/preconnect';
import { ONETRUST_KEY } from '~/constants/onetrustKey';
import { flatten } from 'lodash';
import { stringify } from 'qs';

export default async ({ $config, env, $railsHost, route, store }) => {
  store.commit('utils/clearMeta');
  store.commit('utils/setMeta', route.meta[0]);

  const $i18n = store.$i18n;
  const pageHead = store.getters['pageContent/getHead'];
  const options = metaKey(route, 'metaTagOptions') || {};
  const scope = options.scope || metaKey(route, 'jumbotronScope') || i18nScope(route, $i18n);

  let description = pageHead && pageHead.description !== null ? pageHead.description : options.description;
  if (!description && Object.keys($i18n.t(`pageInfo.${scope}`)).find(k => k === 'description')) {
    description = $i18n.t(`pageInfo.${scope}.description`, metaKey(route, 'jumbotronParams'));
  } else if (!description) {
    description = $i18n.t('pageInfo.description');
  }
  const preconnects = $config.environment === 'production' ? PRECONNECT.map(href => { return { href, rel: 'preconnect' }; }) : [];
  const prefetches = $config.environment === 'production' ? PRECONNECT.map(href => { return { href, rel: 'dns-prefetch' }; }) : [];
  const link = prefetches.concat(preconnects).concat([{ color: '#70bf4a', href: '/safari-pinned-tab.svg', rel: 'mask-icon' }]);
  const canonicalUrl = sanitizeCanonicalUrl($railsHost, route);
  link.push({ href: canonicalUrl, rel: 'canonical' });
  const title = (pageHead ? pageHead.title : null) || options.title || $i18n.t(`pageInfo.${scope}.title`, metaKey(route, 'jumbotronParams'));

  let meta = [tag('google-site-verification', env.GOOGLE_SITE_VERIFICATION),
                tag('theme-color', '#56c65d'),
                tag('viewport', 'width=device-width, initial-scale=1.0, maximum-scale=1'),
                { content: env.FACEBOOK_APP_ID, hid: 'fb:app_id', property: 'fb:app_id' },
                { content: env.FACEBOOK_PAGE_ID, hid: 'fb:pages', property: 'fb:pages' }];

  const cardImage = (pageHead && pageHead.image) || options.image || 'index';
  const card = options.card || require(`~/assets/images/social/twitter-cards/${cardImage}.png`);

  // Robots Meta
  if (['/stage/', '/your-results/', '/apply/', '/thank-you/', '/customer-satisfaction/', '/speed-test/'].some(p => route.path.includes(p))) {
    meta.push(tag('robots', 'noindex'));
  } else {
    meta.push(tag('robots', 'index'));
  }

  // Twitter Meta
  meta.push(tag('twitter:creator', '@bonkers_ie'));
  meta.push(tag('twitter:site', '@bonkers_ie'));
  meta.push(tag('twitter:title', title));
  meta.push(tag('twitter:description', description));
  meta.push(tag('twitter:dnt', 'on'));
  meta.push(tag('twitter:card', 'summary_large_image'));
  meta.push(tag('twitter:image', card));

  // HTML Meta
  meta.push(tag('description', description));
  meta.push(tag('image_src', card));
  meta.push(tag('title', title));

  // Open Graph Meta
  meta.push(ogTag('og:type', 'website'));
  meta.push(ogTag('og:site_name', 'bonkers.ie'));
  meta.push(ogTag('og:title', title));
  meta.push(ogTag('og:image', card));
  meta.push(ogTag('og:image:width', '470'));
  meta.push(ogTag('og:image:height', '264'));
  meta.push(ogTag('og:url', canonicalUrl));
  if (options.ogType) {
    meta.push(ogTag('og:type', options.ogType));
  }
  if (description) {
    meta.push(ogTag('og:description', description));
  }
  if (options.videoUrl) {
    meta.push(ogTag('og:video', options.videoUrl));
  }

  let script = [];
  const feefo = { ...store.getters['pageContent/getFeefo'] };
  if (feefo.summary) {
    const feefoData = feefo.summary.rating;
    const aggregateRating = {
      '@type': 'AggregateRating',
      bestRating: feefoData.max,
      ratingValue: feefoData.rating,
      ratingCount: feefoData.service.count,
      worstRating: feefoData.min
    };
    const ldData = { ...BONKERS_LD_DATA, ...{ aggregateRating } };
    script.push(ldScript(ldData));
  }

  // script.push({ src: 'https://cdn.cookielaw.org/consent/d468b602-cb40-4be7-b15c-da65e70c7073/OtAutoBlock.js',
  //               type: 'text/javascript' });
  script.push({ charset: 'UTF-8',
                'data-domain-script': ONETRUST_KEY,
                src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
                type: 'text/javascript',
                async: true });
  script.push({ class: 'optanon-category-C0002',
                innerHTML: "window.dispatchEvent(new Event('C0002-active'))",
                type: 'text/plain',
                async: true });
  script.push({ class: 'optanon-category-C0004',
    innerHTML: "window.dispatchEvent(new Event('C0004-active'))",
                type: 'text/plain',
                async: true });
  if ($config.environment === 'production') {
    script.push({ class: 'optanon-category-C0002',
                  innerHTML: `(function(h,o,t,j,a,r){ h.hj=h.hj||function(){
                              (h.hj.q=h.hj.q||[]).push(arguments)};
                              h._hjSettings={hjid:108166,hjsv:6};
                              a=o.getElementsByTagName('head')[0];
                              r=o.createElement('script');r.async=1;
                              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                              a.appendChild(r);
                              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
                  type: 'text/plain',
                  async: true });
  }

  const lds = metaKey(route, 'ldJSON');
  if (lds) {
    flatten([lds]).forEach(ld => script.push(ldScript(ld)));
  }

  const breadcrumbs = store.getters['pageContent/getStructuredBreadcrumbs'];
  if (breadcrumbs) {
    script.push(ldScript(breadcrumbs));
  }
  store.commit('pageContent/setHead', { description, meta, link, script, title, __dangerouslyDisableSanitizers: ['script'] });
};

const ldScript = (content) => {
  return { defer: true,
           innerHTML: ((typeof content === 'string') ? content : JSON.stringify(content)),
           type: 'application/ld+json' };
};

const tag = (name, content) => {
  return { content, hid: name, name };
};

const ogTag = (name, content) => {
  return { content, hid: name, property: name };
};

const sanitizeCanonicalUrl = (railsHost, route) => {
  const allowQueries = ['page', 'product'];
  const clearedQueryArray = Object.entries(route.query).filter(([key]) => allowQueries.some(aq => key.includes(aq)));
  const clearedQueryObj = Object.fromEntries(clearedQueryArray);
  const passingQueries = stringify({ ...clearedQueryObj });
  if (clearedQueryArray.length > 0) {
    return `${railsHost()}${route.path}?${passingQueries}`;
  } else {
    return `${railsHost()}${route.path}`;
  }
};
